import moment from "moment";
import Text from "../../components/Text";
import Container from "../../components/Container";
import useAuthHttp from "../../hooks/useAuthHttp";
import AnalysisContainer from "./AnalysisContainer";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Currency from "../../components/Currency";
import { DataTable } from "primereact/datatable";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import PercentageText from "../../components/PercentageText";

interface IRealEstateFundsResponse {
    fii_analysis: {
        user_id: string;
        total_value: number;
        total_payout: number;
        total_result: number;
        current_yield: number;
        current_pvp: number;
        total_result_no_payout: number;
        fii_list: {
            asset_user_id: string;
            asset_id: string;
            asset_type: string;
            asset_sub_type: string | null;
            asset_name: string;
            asset_description: string;
            total_value: number;
            total_payout: number;
            total_result: number;
            current_yield: number;
            current_pvp: number;
            total_result_no_payout: number;
            asset_buy_price_average: number;
            asset_buy_qtt: number;
            asset_current_value: number;
            asset_current_price_average: number;
        }[];
    };
}

const RealEstateFunds = () => {
    const authHttp = useAuthHttp();
    const [period, setPeriod] = useState<Date[]>([]);

    const realEstateFundsQuery = useQuery<IRealEstateFundsResponse>({
        queryKey: [
            "earnings",
            period.length == 2 ? moment(period[0]).format("YYYY-MM-DD") : "",
            period.length == 2 ? moment(period[1]).format("YYYY-MM-DD") : ""
        ],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (period.length == 2) {
                searchParams.append("start_date", moment(period[0]).format("YYYY-MM-DD"));
                searchParams.append("end_date", moment(period[1]).format("YYYY-MM-DD"));
            }

            return authHttp.doGet<IRealEstateFundsResponse>(`analysis/fiis?${searchParams.toString()}`);
        }
    });

    return (
        <AnalysisContainer
            loading={realEstateFundsQuery.isLoading}
            period={period}
            setPeriod={setPeriod}
            showExport={{ filename: "fundos" }}
        >
            <Container flexColumn fullWidth gap={3}>
                <Container wrap gap={3} fullWidth>
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Valor Total em FIIs</Text>
                        <Currency value={realEstateFundsQuery.data?.fii_analysis.total_value ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-900 text-lg" color="primary">{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Proventos Recebidos</Text>
                        <Currency value={realEstateFundsQuery.data?.fii_analysis.total_payout ?? 0}>
                            {(value) => (
                                <Text className="font-bold text-lg" color="primary">{value}</Text>
                            )}
                        </Currency>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Resultado Com Proventos</Text>

                        <Container between>
                            <Currency value={realEstateFundsQuery.data?.fii_analysis.total_result ?? 0}>
                                {(value) => (
                                    <Text className="font-bold text-lg" color={(realEstateFundsQuery.data?.fii_analysis.total_result ?? 0) <= 0 ? "attention" : "green-500"}>{value}</Text>
                                )}
                            </Currency>

                            <PercentageText color={(realEstateFundsQuery.data?.fii_analysis.total_result ?? 0) <= 0 ? "attention" : "green-500"}>
                                {((realEstateFundsQuery.data?.fii_analysis.total_result ?? 0) / (realEstateFundsQuery.data?.fii_analysis.total_value ?? 0)) * 100}   
                            </PercentageText>
                        </Container>
                    </Container>
                </Container>

                <Container wrap gap={3} fullWidth>
                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Yield Atual</Text>
                        <PercentageText fontWeight="bold" size="lg">
                            {realEstateFundsQuery.data?.fii_analysis.current_yield ?? 0}
                        </PercentageText>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">P/VP</Text>
                        <Text className="font-bold text-lg" color="900">{realEstateFundsQuery.data?.fii_analysis.current_pvp ?? 0}</Text>
                    </Container>

                    <Container className="flex flex-1 bg-white border-1 border-gray-300 gap-2 p-3 border-round justify-center items-center" flexColumn>
                        <Text className="text-sm text-900">Resultado Sem Distribuição</Text>

                        <Container between>
                            <Currency value={realEstateFundsQuery.data?.fii_analysis.total_result_no_payout ?? 0}>
                                {(value) => (
                                    <Text className="font-bold text-lg" color={(realEstateFundsQuery.data?.fii_analysis.total_result_no_payout ?? 0) <= 0 ? "attention" : "green-500"}>{value}</Text>
                                )}
                            </Currency>

                            <PercentageText color={(realEstateFundsQuery.data?.fii_analysis.total_result_no_payout ?? 0) <= 0 ? "attention" : "green-500"}>
                                {((realEstateFundsQuery.data?.fii_analysis.total_result_no_payout ?? 0) / (realEstateFundsQuery.data?.fii_analysis.total_value ?? 0)) * 100}   
                            </PercentageText>
                        </Container>
                    </Container>
                </Container>

                <Card
                    title="Ativos"
                    subTitle="Carteira de fundos imobiliários"
                >
                    <Container className="table-responsive w-full">
                        <DataTable
                            value={realEstateFundsQuery.data?.fii_analysis.fii_list ?? []}
                            className="w-full"
                            emptyMessage="Nenhum registro encontrado"
                        >
                            <Column field="asset_name" header="Ativo" />
                            <Column 
                                field="asset_quantity" 
                                header="Quantidade" 
                                body={(rowData) => (
                                    <span>{rowData.asset_quantity ?? 0}</span>
                                )}
                                style={{ minWidth: "250px" }}
                            />

                            <Column
                                field="asset_buy_price_average"
                                header="Preço médio de compra"
                                body={(rowData) => (
                                    <Currency value={rowData.asset_buy_price_average ?? 0}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "225px" }}
                            />

                            <Column
                                field="asset_current_value"
                                header="Valor atual"
                                body={(rowData) => (
                                    <Currency value={rowData.asset_current_value ?? 0}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                            />
                            <Column
                                field="asset_current_price_average"
                                header="Preço médio atual"
                                body={(rowData) => (
                                    <Currency value={rowData.asset_current_price_average ?? 0}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "185px" }}
                            />
                            <Column 
                                field="asset_actual_price" 
                                header="Preço Atual" 
                                body={(rowData) => (
                                    <Currency value={rowData.asset_actual_price ?? 0}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "175px" }}
                            />
                            <Column 
                                field="total_value" 
                                header="Valor Total" 
                                body={(rowData) => (
                                    <Currency value={rowData.total_value}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                            />
                            <Column 
                                field="total_payout" 
                                header="Distribuição" 
                                body={(rowData) => (
                                    <Currency value={rowData.total_payout}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                            />
                            <Column 
                                field="total_result_no_payout" 
                                header="Resultado sem distribuição" 
                                body={(rowData) => (
                                    <Currency value={rowData.total_result_no_payout}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "250px" }}
                            />
                            <Column 
                                field="total_result" 
                                header="Resultado com distribuição" 
                                body={(rowData) => (
                                    <Currency value={rowData.total_result}>
                                        {(value) => (
                                            <span>{value}</span>
                                        )}
                                    </Currency>
                                )}
                                style={{ minWidth: "250px" }}
                            />
                            <Column 
                                field="current_yield" 
                                header="Yield Atual" 
                                body={(rowData) => (
                                    <PercentageText fontWeight="normal">
                                        {rowData.current_yield}
                                    </PercentageText>
                                )}
                                style={{ minWidth: "175px" }}
                            />
                            <Column 
                                field="current_pvp"
                                header="P/VP"
                            />
                        </DataTable>
                    </Container>
                </Card>
            </Container>
        </AnalysisContainer>
    )
}

export default RealEstateFunds;
